/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { showError } from '@utils';
import { boardsServices } from '@services';
import { BOARDS_ID } from '@constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees, useServicePoints } from 'hooks/common';
import { TRAFFIC_OFFENSES_PAGE_SIZE } from './constants';
import {
  getColumns,
  filterTrafficOffensesAssignees,
  normaliseTrafficOffensesTasks,
  getTrafficOffensesParamsAndPayload,
} from './utils';

export const useTrafficOffensesOperations = () => {
  const {
    assignees: cachedAssignees,
    isFetching: isAssigneesFetching,
    isFetched: isAssigneesFetched,
    assigneesWithNone,
  } = useAssignees();
  const { servicePoints, isFetching: isServicePointsFetching } =
    useServicePoints();
  const router = useRouter();

  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || TRAFFIC_OFFENSES_PAGE_SIZE;
  const orderBy = router.query?.orderBy || 'createdAt';
  const order = router.query?.order || 'desc';
  const assignee = router.query?.assignee;
  const query = router.query?.query;

  const params = {
    page,
    pageSize: parseInt(pageSize, 10) || TRAFFIC_OFFENSES_PAGE_SIZE,
    orderBy,
    order,
    query,
  };

  const { cleanParams, payload } = useMemo(
    () => getTrafficOffensesParamsAndPayload({ params, assignee, query }),
    [
      params?.page,
      params?.pageSize,
      params?.orderBy,
      params?.order,
      assignee,
      query,
    ]
  );

  const trafficOffensesResponse = useQuery(
    [
      QUERY_KEYS.GET_TRAFFIC_OFFENSES_KEY,
      { ...cleanParams, assignee },
      payload,
    ],
    () => {
      return boardsServices.getBoardTasks({
        boardId: BOARDS_ID.TRAFFIC_OFFENSES,
        params: cleanParams,
        payload,
      });
    },
    {
      enabled: isAssigneesFetched,
    }
  );

  const loading =
    trafficOffensesResponse?.isFetching ||
    isAssigneesFetching ||
    isServicePointsFetching;

  const assignees = filterTrafficOffensesAssignees(cachedAssignees);

  const trafficOffensesTasks = normaliseTrafficOffensesTasks(
    trafficOffensesResponse?.data?.data || [],
    assignees || []
  );

  useEffect(() => {
    if (trafficOffensesResponse.isError) {
      showError(trafficOffensesResponse?.error);
    }
  }, [trafficOffensesResponse.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    page,
    pageSize: parseInt(pageSize, 10) || TRAFFIC_OFFENSES_PAGE_SIZE,
    total: trafficOffensesResponse?.data?.meta?.total,
    loading,
    trafficOffenses: trafficOffensesTasks?.tasks,
    assignees,
    servicePoints,
    assigneesWithNone,
    columns: getColumns(trafficOffensesTasks?.tasks || []),
    trafficOffensesQueryParams: cleanParams,
    trafficOffensesPayload: payload,
    getTrafficOffenses: trafficOffensesResponse.refetch,
  };
};

export default useTrafficOffensesOperations;
